import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MeetingVideoModal from '../components/MeetingVideoModal_Universal';
import { 
  Building2, 
  Users, 
  Calendar, 
  FileText, 
  Vote,
  Video,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MapPin,
  Phone,
  Mail,
  Settings,
  Download,
  Upload,
  Eye,
  Shield,
  Hash,
  ExternalLink,
  Star,
  Map,
  TrendingUp,
  Euro,
  Edit,
  Trash2
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/notifications/ToastProvider';
import LiveVotingModal from '../components/LiveVotingModal';
import MeetingManagementModal from '../components/MeetingManagementModal';

// Hash-Chain Service für Premium Features
const HashChainService = {
  async loadHashChainStats(communities) {
    try {
      const API_BASE_URL = 'http://217.154.242.134:5000/api/vps/weg';
      
      // Für jede Community Hash-Chain Stats laden
      const statsPromises = (communities || []).map(async (community) => {
        try {
          const response = await fetch(`${API_BASE_URL}/stats/${community.id}`);
          if (response.ok) {
            return await response.json();
          }
          return null;
        } catch (error) {
          console.warn(`Hash-Chain stats for community ${community.id}:`, error);
          return null;
        }
      });
      
      const stats = await Promise.all(statsPromises);
      console.log('Hash-Chain Stats loaded:', stats);
      return stats;
    } catch (error) {
      console.warn('Hash-Chain service not available:', error);
      return [];
    }
  },

  async enablePremium() {
    localStorage.setItem('weg_premium_enabled', 'true');
    console.log('🎉 Blockchain-Premium aktiviert!');
  }
};

// Globale loadHashChainStats Funktion für Kompatibilität
const loadHashChainStats = HashChainService.loadHashChainStats;

const WEGManagementPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('communities');
  const [communities, setCommunities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [motions, setMotions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showVotingModal, setShowVotingModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Premium state
  const [isPremium, setIsPremium] = useState(() => {
    return localStorage.getItem('weg_premium_enabled') === 'true';
  });

  // Roadmap Modal state
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load communities with related data
      const { data: communitiesData, error: communitiesError } = await masterSupabase
        .from('hoa_communities')
        .select(`
          *,
          hoa_units(id, label, area_sqm, ownership_share_ppm),
          hoa_memberships(
            id, 
            role, 
            ownership_share_ppm,
            hoa_members(id, full_name, email)
          ),
          hoa_meetings(id, title, scheduled_at, quorum_met)
        `);

      if (communitiesError) {
        console.error('Communities error:', communitiesError);
        setCommunities([]);
      } else {
        setCommunities(communitiesData || []);
      }

      // Load recent meetings
      const { data: meetingsData, error: meetingsError } = await masterSupabase
        .from('hoa_meetings')
        .select(`
          *,
          hoa_communities(name)
        `)
        .order('scheduled_at', { ascending: false })
        .limit(10);

      if (meetingsError) {
        console.error('Meetings error:', meetingsError);
        setMeetings([]);
      } else {
        setMeetings(meetingsData || []);
      }

      // Load recent motions
      const { data: motionsData, error: motionsError } = await masterSupabase
        .from('hoa_motions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (motionsError) {
        console.error('Motions error:', motionsError);
        setMotions([]);
      } else {
        setMotions(motionsData || []);
      }

      // Load members
      const { data: membersData, error: membersError } = await masterSupabase
        .from('hoa_members')
        .select(`
          *,
          hoa_memberships(
            id,
            role,
            ownership_share_ppm,
            is_active,
            hoa_communities(name)
          )
        `)
        .limit(50);

      if (membersError) {
        console.error('Members error:', membersError);
        setMembers([]);
      } else {
        setMembers(membersData || []);
      }

    } catch (error) {
      console.error('Load data error:', error);
      if (addToast) {
        addToast('Fehler beim Laden der Daten', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function for safe array operations
  const safeFilter = (array, filterFn) => {
    return (array || []).filter(filterFn);
  };

  const safeMap = (array, mapFn) => {
    return (array || []).map(mapFn);
  };

  // Filter functions
  const filteredCommunities = safeFilter(communities, community =>
    community.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMeetings = safeFilter(meetings, meeting =>
    meeting.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.hoa_communities?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMotions = safeFilter(motions, motion =>
    motion.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    motion.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMembers = safeFilter(members, member =>
    member.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Statistics calculations with safe operations
  const activeCommunities = safeFilter(communities, c => c.is_active).length;
  const upcomingMeetings = safeFilter(meetings, m => new Date(m.scheduled_at) > new Date()).length;
  const openMotions = safeFilter(motions, m => m.status === 'open').length;
  const totalMembers = members.length;

  // Handle video modal with Jitsi Meet
  const handleVideoJoin = (meeting) => {
    // Create Jitsi Meet room URL
    const roomName = `weg-meeting-${meeting.id}-${Date.now()}`;
    const jitsiUrl = `https://meet.jit.si/${roomName}`;
    
    // Open Jitsi Meet in new window
    const jitsiWindow = window.open(
      jitsiUrl,
      'jitsi-meet',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (jitsiWindow) {
      if (addToast) {
        addToast(`Video-Meeting gestartet: ${meeting.title}`, 'success');
      }
    } else {
      if (addToast) {
        addToast('Popup-Blocker verhindert Video-Meeting. Bitte erlauben Sie Popups.', 'error');
      }
    }
    
    setSelectedMeeting(meeting);
    setShowVideoModal(true);
  };

  // Handle meeting management
  const handleMeetingManagement = (meeting) => {
    setSelectedMeeting(meeting);
    setShowMeetingModal(true);
  };

  // Handle voting
  const handleVoting = (motion) => {
    setSelectedMotion(motion);
    setShowVotingModal(true);
  };

  // Create demo meeting for testing
  const createDemoMeeting = async () => {
    try {
      const demoMeeting = {
        title: 'Demo WEG-Versammlung',
        description: 'Test-Versammlung für Video-Integration',
        scheduled_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
        community_id: communities[0]?.id || null,
        agenda: JSON.stringify([
          { id: 1, title: 'Begrüßung', duration: 10 },
          { id: 2, title: 'Video-Test', duration: 15 },
          { id: 3, title: 'Abstimmung testen', duration: 20 }
        ])
      };

      const { data, error } = await masterSupabase
        .from('hoa_meetings')
        .insert([demoMeeting])
        .select();

      if (error) throw error;

      if (addToast) {
        addToast('Demo-Meeting erstellt! Video-Buttons sind jetzt sichtbar.', 'success');
      }
      
      // Reload data to show new meeting
      loadData();
    } catch (error) {
      console.error('Demo meeting creation error:', error);
      if (addToast) {
        addToast('Fehler beim Erstellen des Demo-Meetings', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Vote className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  WEG Management
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Digitale Wohnungseigentümergemeinschaften
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Premium Status */}
              <div className="flex items-center space-x-2">
                {isPremium ? (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-sm font-medium">
                    <Shield className="w-4 h-4" />
                    <span>Blockchain-Premium</span>
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      setIsPremium(true);
                      localStorage.setItem('weg_premium_enabled', 'true');
                      if (addToast) {
                        addToast('🎉 Blockchain-Premium aktiviert! Hash-Chain Features verfügbar.', 'success');
                      }
                      await loadHashChainStats(communities);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Premium upgraden
                  </button>
                )}
              </div>

              <button 
                onClick={() => {
                  // Export WEG data as CSV/PDF
                  const exportWEGData = () => {
                    try {
                      // Create CSV content
                      const csvContent = [
                        ['WEG Name', 'Adresse', 'Einheiten', 'Aktive Mitglieder', 'Status'],
                        ...communities.map(weg => [
                          weg.name || 'Unbekannt',
                          weg.address || 'Keine Adresse',
                          weg.total_units || 0,
                          (weg.hoa_memberships || []).filter(m => m.is_active).length,
                          weg.is_active ? 'Aktiv' : 'Inaktiv'
                        ])
                      ].map(row => row.join(',')).join('\\n');

                      // Create and download file
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      const url = URL.createObjectURL(blob);
                      link.setAttribute('href', url);
                      link.setAttribute('download', `weg-export-${new Date().toISOString().split('T')[0]}.csv`);
                      link.style.visibility = 'hidden';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);

                      if (addToast) {
                        addToast('WEG-Daten erfolgreich exportiert!', 'success');
                      }
                    } catch (error) {
                      console.error('Export error:', error);
                      if (addToast) {
                        addToast('Fehler beim Exportieren der Daten', 'error');
                      }
                    }
                  };
                  exportWEGData();
                }}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              
              <button 
                onClick={() => setShowRoadmapModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Map className="w-4 h-4 mr-2" />
                Roadmap
              </button>
              
              <button 
                onClick={() => {
                  // Create new WEG community
                  const createNewWEG = async () => {
                    try {
                      const newWEG = {
                        name: `Demo WEG ${Date.now()}`,
                        address: 'Musterstraße 123, 12345 Musterstadt',
                        total_units: 12,
                        is_active: true,
                        created_at: new Date().toISOString()
                      };

                      const { data, error } = await masterSupabase
                        .from('hoa_communities')
                        .insert([newWEG])
                        .select();

                      if (error) throw error;

                      if (addToast) {
                        addToast(`Neue WEG "${newWEG.name}" erfolgreich erstellt!`, 'success');
                      }
                      
                      // Reload data to show new WEG
                      loadData();
                    } catch (error) {
                      console.error('WEG creation error:', error);
                      if (addToast) {
                        addToast('Fehler beim Erstellen der neuen WEG', 'error');
                      }
                    }
                  };
                  createNewWEG();
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neue WEG
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktive WEGs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeCommunities}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {activeCommunities === 0 ? "Keine WEGs" : `${activeCommunities} aktiv`}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Offene Beschlüsse</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{openMotions}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {openMotions === 0 ? "Alle abgestimmt" : "Abstimmung läuft"}
                </p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Vote className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Anstehende Versammlungen</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{upcomingMeetings}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {upcomingMeetings === 0 ? "Keine geplant" : "Nächste Termine"}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamte Eigentümer</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalMembers}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {totalMembers === 0 ? "0 Einheiten verwaltet" : "Registrierte Mitglieder"}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="WEGs, Versammlungen oder Beschlüsse suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
            />
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button>
        </div>

        {/* Status Indicator */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Standard WEG-Verwaltung</span>
            </div>
            {isPremium && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-blue-600 dark:text-blue-400">Blockchain-Premium aktiv</span>
              </div>
            )}
          </div>
          
          {!isPremium && (
            <button
              onClick={async () => {
                setIsPremium(true);
                localStorage.setItem('weg_premium_enabled', 'true');
                if (addToast) {
                  addToast('🎉 Blockchain-Premium aktiviert! Hash-Chain Features verfügbar.', 'success');
                }
                await loadHashChainStats(communities);
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
            >
              <Star className="w-4 h-4" />
              <span>Blockchain-Premium upgraden</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'communities', label: 'WEG-Gemeinschaften', icon: Building2 },
                { id: 'meetings', label: 'Versammlungen', icon: Calendar },
                { id: 'motions', label: 'Beschlüsse', icon: Vote },
                { id: 'members', label: 'Eigentümer', icon: Users }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Communities Tab */}
            {activeTab === 'communities' && (
              <div>
                {filteredCommunities.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Neue WEG hinzufügen</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Erstellen Sie eine neue Wohnungseigentümergemeinschaft
                    </p>
                    <div className="mt-6">
                      <button 
                        onClick={createDemoMeeting}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        WEG erstellen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {safeMap(filteredCommunities, (community) => (
                      <div key={community.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{community.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{community.address}</p>
                            
                            <div className="mt-4 space-y-2">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Building2 className="w-4 h-4 mr-2" />
                                <span>{(community.hoa_units || []).length} Einheiten</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Users className="w-4 h-4 mr-2" />
                                <span>{(community.hoa_memberships || []).length} Eigentümer</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span>{(community.hoa_meetings || []).length} Versammlungen</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                              <MoreVertical className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="mt-6 flex space-x-3">
                          <button className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <Eye className="w-4 h-4 mr-2" />
                            Details
                          </button>
                          <button onClick={() => handleMeetingManagement(community)} className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                            <Settings className="w-4 h-4 mr-2" />
                            Verwalten
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Meetings Tab */}
            {activeTab === 'meetings' && (
              <div>
                {filteredMeetings.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Keine Versammlungen</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Erstellen Sie Ihre erste WEG-Versammlung
                    </p>
                    <div className="mt-6">
                      <button 
                        onClick={createDemoMeeting}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Versammlung planen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {safeMap(filteredMeetings, (meeting) => (
                      <div key={meeting.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{meeting.title}</h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                new Date(meeting.scheduled_at) > new Date()
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-black/30 dark:text-gray-400'
                              }`}>
                                {new Date(meeting.scheduled_at) > new Date() ? 'Geplant' : 'Beendet'}
                              </span>
                            </div>
                            
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>{new Date(meeting.scheduled_at).toLocaleString('de-DE')}</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Building2 className="w-4 h-4 mr-2" />
                                <span>{meeting.hoa_communities?.name || 'Unbekannte WEG'}</span>
                              </div>
                              {meeting.hoa_motions && (
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                  <Vote className="w-4 h-4 mr-2" />
                                  <span>{(meeting.hoa_motions || []).length} Beschlüsse</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-6 flex space-x-3">
                          <button 
                            onClick={() => handleVideoJoin(meeting)}
                            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <Video className="w-4 h-4 mr-2" />
                            🎥 Video beitreten
                          </button>
                          <button 
                            onClick={() => handleMeetingManagement(meeting)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Teilnehmen
                          </button>
                          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors">
                            <Eye className="w-4 h-4 mr-2" />
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Motions Tab */}
            {activeTab === 'motions' && (
              <div>
                {filteredMotions.length === 0 ? (
                  <div className="text-center py-12">
                    <Vote className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Keine Beschlüsse</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Beschlüsse werden in Versammlungen erstellt
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {safeMap(filteredMotions, (motion) => (
                      <div key={motion.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{motion.title}</h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                motion.status === 'open'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : motion.status === 'passed'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {motion.status === 'open' ? 'Abstimmung läuft' : 
                                 motion.status === 'passed' ? 'Angenommen' : 'Abgelehnt'}
                              </span>
                            </div>
                            
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{motion.description}</p>
                            
                            <div className="mt-3 space-y-1">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>Frist: {new Date(motion.vote_deadline).toLocaleString('de-DE')}</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Building2 className="w-4 h-4 mr-2" />
                                <span>{motion.hoa_communities?.name || 'Unbekannte WEG'}</span>
                              </div>
                            </div>

                            {/* Voting Progress */}
                            {motion.hoa_votes && motion.hoa_votes.length > 0 && (
                              <div className="mt-4">
                                <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  <span>Abstimmungsstand</span>
                                  <span>{(motion.hoa_votes || []).length} Stimmen</span>
                                </div>
                                
                                {/* Vote breakdown */}
                                <div className="space-y-2">
                                  {['yes', 'no', 'abstain'].map((voteType) => {
                                    const votes = safeFilter(motion.hoa_votes || [], v => v.vote === voteType);
                                    const count = votes.length;
                                    const percentage = motion.hoa_votes.length > 0 ? (count / motion.hoa_votes.length * 100) : 0;
                                    
                                    return (
                                      <div key={voteType} className="flex items-center space-x-3">
                                        <span className="w-16 text-sm text-gray-600 dark:text-gray-400 capitalize">
                                          {voteType === 'yes' ? 'Ja' : voteType === 'no' ? 'Nein' : 'Enthaltung'}
                                        </span>
                                        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                          <div 
                                            className={`h-2 rounded-full ${
                                              voteType === 'yes' ? 'bg-green-500' : 
                                              voteType === 'no' ? 'bg-red-500' : 'bg-gray-400'
                                            }`}
                                            style={{ width: `${percentage}%` }}
                                          ></div>
                                        </div>
                                        <span className="w-12 text-sm text-gray-600 dark:text-gray-400 text-right">
                                          {count}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-6 flex space-x-3">
                          {motion.status === 'open' && (
                            <button 
                              onClick={() => handleVoting(motion)}
                              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              <Vote className="w-4 h-4 mr-2" />
                              Abstimmen
                            </button>
                          )}
                          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors">
                            <Eye className="w-4 h-4 mr-2" />
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
              <div>
                {filteredMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Keine Eigentümer</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Eigentümer werden automatisch bei WEG-Erstellung hinzugefügt
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {safeMap(filteredMembers, (member) => (
                      <div key={member.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                              {member.full_name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                            
                            <div className="mt-3 space-y-1">
                              {member.phone && (
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                  <Phone className="w-4 h-4 mr-2" />
                                  <span>{member.phone}</span>
                                </div>
                              )}
                              
                              {/* Memberships */}
                              {(member.hoa_memberships || []).map((membership, index) => (
                                <div key={index} className="text-sm">
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Building2 className="w-4 h-4 mr-2" />
                                    <span>{membership.hoa_communities?.name}</span>
                                  </div>
                                  <div className="ml-6 text-xs text-gray-500 dark:text-gray-500">
                                    {membership.role} • {(membership.ownership_share_ppm / 10000).toFixed(2)}% Anteil
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-6 flex space-x-3">
                          <button className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <Mail className="w-4 h-4 mr-2" />
                            Kontakt
                          </button>
                          <button className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                            <Eye className="w-4 h-4 mr-2" />
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedMeeting && (
        <MeetingVideoModal
          meeting={selectedMeeting}
          onClose={() => {
            setShowVideoModal(false);
            setSelectedMeeting(null);
          }}
        />
      )}

      {/* Meeting Management Modal */}
      {showMeetingModal && selectedMeeting && (
        <MeetingManagementModal
          meeting={selectedMeeting}
          onClose={() => {
            setShowMeetingModal(false);
            setSelectedMeeting(null);
          }}
          onUpdate={loadData}
        />
      )}

      {/* Voting Modal */}
      {showVotingModal && selectedMotion && (
        <LiveVotingModal
          motion={selectedMotion}
          onClose={() => {
            setShowVotingModal(false);
            setSelectedMotion(null);
          }}
          onUpdate={loadData}
        />
      )}

      {/* Roadmap Modal */}
      {showRoadmapModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 ${isFullscreen ? 'p-0' : 'p-2 sm:p-4'}`}>
          <div className={`bg-white dark:bg-slate-900 rounded-lg shadow-xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'max-w-6xl w-full h-[90vh] sm:h-[95vh]'}`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-500 to-blue-600 flex-shrink-0">
              <div className="flex items-center space-x-3 text-white">
                <Map className="w-5 h-5 sm:w-6 sm:h-6" />
                <div>
                  <h2 className="text-base sm:text-lg font-bold">WEG/DAO-Verwaltung Roadmap</h2>
                  <p className="text-xs sm:text-sm opacity-90">Slide {currentSlide + 1} von {roadmapSlides.length}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  {isFullscreen ? <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" /> : <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />}
                </button>
                <button
                  onClick={() => setShowRoadmapModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Slide Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
              <div dangerouslySetInnerHTML={{ __html: roadmapSlides[currentSlide]?.content || '' }} />
            </div>

            {/* Navigation - Fixed at bottom */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-black flex-shrink-0">
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-200 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                ← Zurück
              </button>
              
              <div className="flex space-x-1 sm:space-x-2">
                {roadmapSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors ${
                      index === currentSlide 
                        ? 'bg-purple-600' 
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCurrentSlide(Math.min(roadmapSlides.length - 1, currentSlide + 1))}
                disabled={currentSlide === roadmapSlides.length - 1}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Weiter →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Roadmap Slides Data
const roadmapSlides = [
  {
    title: "WEG/DAO-Verwaltung: Roadmap 2025-2026",
    content: `
      <div class="text-center">
        <h1 class="text-4xl font-bold text-gray-900 dark:text-white mb-6">WEG/DAO-Verwaltung</h1>
        <h2 class="text-2xl text-purple-600 dark:text-purple-400 mb-8">Roadmap 2025-2026</h2>
        <div class="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 p-8 rounded-xl">
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-4">
            Transformation traditioneller Wohnungseigentümergemeinschaften zu modernen DAOs
          </p>
          <p class="text-gray-600 dark:text-gray-400">
            Mit Blockchain-Technologie, KI-gestützter Verwaltung und vollautomatisierten Prozessen
          </p>
        </div>
      </div>
    `
  },
  {
    title: "Aktueller Status - September 2025",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Aktueller Status - September 2025</h1>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-4">✅ Bereits implementiert</h3>
            <ul class="space-y-3 text-gray-700 dark:text-gray-300">
              <li class="flex items-center"><span class="text-green-600 mr-2">●</span> Video-Meetings & Versammlungen</li>
              <li class="flex items-center"><span class="text-green-600 mr-2">●</span> Blockchain-sichere Protokolle</li>
              <li class="flex items-center"><span class="text-green-600 mr-2">●</span> DAO-Abstimmungssystem</li>
              <li class="flex items-center"><span class="text-green-600 mr-2">●</span> Umfassende HOA-Datenbank</li>
              <li class="flex items-center"><span class="text-green-600 mr-2">●</span> Premium Blockchain-Features</li>
            </ul>
          </div>
          <div class="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">🔧 In Entwicklung</h3>
            <ul class="space-y-3 text-gray-700 dark:text-gray-300">
              <li class="flex items-center"><span class="text-blue-600 mr-2">●</span> KI-gestützte Konfliktprävention</li>
              <li class="flex items-center"><span class="text-blue-600 mr-2">●</span> Automatisierte Fördermittel-Integration</li>
              <li class="flex items-center"><span class="text-blue-600 mr-2">●</span> Erweiterte Kommunikations-Tools</li>
              <li class="flex items-center"><span class="text-blue-600 mr-2">●</span> Mobile App Entwicklung</li>
            </ul>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Killer-Features für Differenzierung",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Killer-Features für Differenzierung</h1>
        <div class="space-y-6">
          <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-purple-800 dark:text-purple-300 mb-3">🤖 Predictive Governance mit KI</h3>
            <p class="text-gray-700 dark:text-gray-300 mb-3">
              KI erkennt Konflikte oder Finanzengpässe frühzeitig und schlägt automatisch Maßnahmen vor
            </p>
            <ul class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Frühwarnung bei niedrigen Rücklagen</li>
              <li>• Automatische Konflikt-Erkennung in Diskussionen</li>
              <li>• Präventive Handlungsempfehlungen</li>
            </ul>
          </div>
          
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-3">💰 Automatisierte Fördermittel-Integration</h3>
            <p class="text-gray-700 dark:text-gray-300 mb-3">
              Smart Contracts verknüpft mit Förderprogrammen (BAFA, KfW) für direkten Geldfluss
            </p>
            <ul class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Automatischer Fördermittel-Antrag</li>
              <li>• Direkte Integration in Rücklagen</li>
              <li>• Sanierungsbeschluss → Förderung</li>
            </ul>
          </div>
          
          <div class="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-blue-800 dark:text-blue-300 mb-3">🎮 Gamification & Impact Scores</h3>
            <p class="text-gray-700 dark:text-gray-300 mb-3">
              Eigentümer sammeln Punkte für Teilnahme und konstruktive Beiträge
            </p>
            <ul class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Transparente Rankings für mehr Beteiligung</li>
              <li>• Belohnungen für schnelle Reaktionen</li>
              <li>• Weniger Blockaden durch Engagement</li>
            </ul>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Monetarisierungs-Streams",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Monetarisierungs-Streams</h1>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-orange-800 dark:text-orange-300 mb-4">📊 Gestaffelte Premium-Pakete</h3>
            <div class="space-y-3">
              <div class="bg-white dark:bg-slate-900 p-3 rounded-lg">
                <h4 class="font-semibold text-gray-800 dark:text-gray-200">Basis</h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">Versammlungen & Abstimmungen</p>
              </div>
              <div class="bg-white dark:bg-slate-900 p-3 rounded-lg">
                <h4 class="font-semibold text-gray-800 dark:text-gray-200">Premium</h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">Blockchain-Sicherung, KI-Insights</p>
              </div>
              <div class="bg-white dark:bg-slate-900 p-3 rounded-lg">
                <h4 class="font-semibold text-gray-800 dark:text-gray-200">Enterprise</h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">Vollständige DAO mit IoT, Energieoptimierung</p>
              </div>
            </div>
          </div>
          
          <div class="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-purple-800 dark:text-purple-300 mb-4">🔗 Cross-Selling & Partnerschaften</h3>
            <ul class="space-y-3 text-gray-700 dark:text-gray-300">
              <li class="flex items-center"><span class="text-purple-600 mr-2">●</span> Versicherungen direkt im Dashboard</li>
              <li class="flex items-center"><span class="text-purple-600 mr-2">●</span> Finanzierung für Sanierungen</li>
              <li class="flex items-center"><span class="text-purple-600 mr-2">●</span> Messdienste & Facility Management</li>
              <li class="flex items-center"><span class="text-purple-600 mr-2">●</span> White-Label Lösung für Verwaltungen</li>
            </ul>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Q4 2025: Erweiterte Features",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Q4 2025: Erweiterte Features</h1>
        <div class="space-y-6">
          <div class="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-indigo-800 dark:text-indigo-300 mb-4">🏢 Erweiterte Eigentümerverwaltung</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Detaillierte Eigentumsanteile-Verwaltung</li>
                <li>• Automatische Kostenverteilung</li>
                <li>• Digitale Eigentümerversammlungen</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Rechtssichere Protokollierung</li>
                <li>• Mehrsprachige Unterstützung</li>
                <li>• Erweiterte Berichtsfunktionen</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-4">🏠 Einheiten-Management</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Digitale Wohnungsakte</li>
                <li>• Wartungshistorie pro Einheit</li>
                <li>• Energieverbrauch-Tracking</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Automatische Schadensmeldung</li>
                <li>• IoT-Sensor Integration</li>
                <li>• Predictive Maintenance</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">💬 Erweiterte Kommunikations-Tools</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• KI-gestützte Moderation</li>
                <li>• Automatische Protokoll-Zusammenfassungen</li>
                <li>• Mehrsprachige Echtzeit-Übersetzung</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Strukturierte Diskussionsforen</li>
                <li>• Push-Benachrichtigungen</li>
                <li>• Deadline-Management</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Q1 2026: Vollständige DAO-Transformation",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Q1 2026: Vollständige DAO-Transformation</h1>
        <div class="space-y-6">
          <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-purple-800 dark:text-purple-300 mb-4">⛓️ Smart Contracts & Automatisierung</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Automatische Beschluss-Ausführung</li>
                <li>• Smart Contract Governance</li>
                <li>• Dezentrale Entscheidungsfindung</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Tokenisierte Stimmrechte</li>
                <li>• Automatische Compliance-Prüfung</li>
                <li>• Blockchain-basierte Transparenz</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-orange-800 dark:text-orange-300 mb-4">🪙 Tokenisierte Stimmrechte</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Eigentumsanteile als NFTs</li>
                <li>• Handelbare Stimmrechte</li>
                <li>• Liquid Democracy</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Delegierte Abstimmungen</li>
                <li>• Gewichtete Entscheidungen</li>
                <li>• Transparente Governance</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-4">🎯 Pilot-DAO Ende 2025</h3>
            <p class="text-gray-700 dark:text-gray-300 mb-3">
              Beschleunigter Rollout mit einer kleinen WEG als Pilot-Projekt für Erfahrungssammlung und Marketing-Case
            </p>
            <ul class="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Vollständige DAO-Funktionalität testen</li>
              <li>• Feedback sammeln und optimieren</li>
              <li>• Erfolgsgeschichte für Marketing nutzen</li>
            </ul>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Q2 2026: Mobile App & API-Ökosystem",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Q2 2026: Mobile App & API-Ökosystem</h1>
        <div class="space-y-6">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">📱 Clara KI Mobile App</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Clara KI Chatbot für direkte Fragen</li>
                <li>• One-Click Abstimmung mit Wallet-Signatur</li>
                <li>• Push-Benachrichtigungen für Deadlines</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• AR-Inspektion & Schadensmeldung</li>
                <li>• Biometrische Authentifizierung</li>
                <li>• Offline-Modus für wichtige Funktionen</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-purple-800 dark:text-purple-300 mb-4">🔗 API-Ökosystem für Drittanbieter</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Buchhaltungssoftware-Integration</li>
                <li>• Facility Management Systeme</li>
                <li>• IoT-Geräte und Smart Home</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Energiemanagementsysteme</li>
                <li>• Versicherungsplattformen</li>
                <li>• Rechtsdienste und Compliance</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-4">🌐 Marketplace Integration</h3>
            <p class="text-gray-700 dark:text-gray-300 mb-3">
              Vollständige Integration mit dem Clara360 Marketplace für nahtlose Dienstleister-Buchung
            </p>
            <ul class="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Direktbuchung von Handwerkern aus DAO-Beschlüssen</li>
              <li>• Automatische Kostenverteilung auf Eigentümer</li>
              <li>• Bewertungssystem für Dienstleister</li>
            </ul>
          </div>
        </div>
      </div>
    `
  },
  {
    title: "Zukunftsvision 2027+",
    content: `
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-8">Zukunftsvision 2027+</h1>
        <div class="space-y-6">
          <div class="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-cyan-800 dark:text-cyan-300 mb-4">🏢 Smart Building Integration</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• IoT-Sensoren für Energieflüsse</li>
                <li>• Predictive Maintenance durch KI</li>
                <li>• Automatische Schadenserkennung</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Optimierte Heizungs-/Kühlungssteuerung</li>
                <li>• Sicherheitssysteme Integration</li>
                <li>• Umweltmonitoring</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-orange-800 dark:text-orange-300 mb-4">💎 Tokenisierte Immobilienwerte</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Eigentumsanteile als handelbare NFTs</li>
                <li>• Fraktionierte Immobilieninvestments</li>
                <li>• Liquidität für Eigentümer</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Automatische Wertsteigerung-Tracking</li>
                <li>• Dezentrale Immobilienbörse</li>
                <li>• Cross-Chain Kompatibilität</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-green-800 dark:text-green-300 mb-4">⚡ Energiegemeinschaften</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Lokale Strommärkte zwischen WEGs</li>
                <li>• Peer-to-Peer Energiehandel</li>
                <li>• Gemeinsame Solaranlagen-Projekte</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Automatische Energieoptimierung</li>
                <li>• Blockchain-basierte Abrechnung</li>
                <li>• Nachhaltigkeits-Tracking</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-6 rounded-xl">
            <h3 class="text-xl font-bold text-purple-800 dark:text-purple-300 mb-4">🌐 Erweitertes Ökosystem</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• ClaraConnect Dienstleister-Marktplatz</li>
                <li>• Dezentrale Finanzierung (DeFi)</li>
                <li>• Internationale WEG-Vernetzung</li>
              </ul>
              <ul class="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• KI-gestützte Rechtsberatung</li>
                <li>• Automatische Compliance-Überwachung</li>
                <li>• Globale Best-Practice Sharing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `
  }
];

export default WEGManagementPage;

